import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';
const FONT = "'Raleway', sans-serif";

const TRIGGER_OPTIONS = [
  { value: 'ENTER', label: 'When entering stage' },
  { value: 'LEAVE', label: 'When leaving stage' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'NOTIFY_ASSIGNEE', label: 'Notify assignee' },
  { value: 'NOTIFY_TEAM',     label: 'Notify team' },
  { value: 'NOTIFY_CUSTOM',   label: 'Notify specific people' },
  { value: 'AUTO_ASSIGN',     label: 'Auto-assign to user' },
  { value: 'SET_DUE_DATE_OFFSET', label: 'Set due date offset' },
  { value: 'MARK_COMPLETE',   label: 'Mark as complete' },
  { value: 'SEND_EMAIL_TEMPLATE', label: 'Send email template' },
  { value: 'WEBHOOK',         label: 'Send webhook' },
];

const s = {
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0.5rem',
    padding: '0.6rem 0.75rem', background: '#fff',
    border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 6,
    fontFamily: FONT,
  },
  select: {
    fontFamily: FONT, fontSize: '0.82rem', padding: '0.3rem 0.5rem',
    border: `1px solid ${BORDER}`, borderRadius: 6, background: '#fff',
    color: NAVY, cursor: 'pointer', outline: 'none',
  },
  input: {
    fontFamily: FONT, fontSize: '0.82rem', padding: '0.3rem 0.5rem',
    border: `1px solid ${BORDER}`, borderRadius: 6, flex: 1,
    color: NAVY, outline: 'none',
  },
  label: {
    fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 2, display: 'block',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 2 },
  toggle: (on) => ({
    width: 34, height: 18, borderRadius: 9, border: 'none', cursor: 'pointer',
    background: on ? '#10b981' : '#d1d5db', position: 'relative',
    transition: 'background 0.2s', flexShrink: 0, marginTop: 2,
  }),
  toggleThumb: (on) => ({
    position: 'absolute', top: 2, left: on ? 16 : 2, width: 14, height: 14,
    borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
  }),
  deleteBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
    fontSize: '1rem', padding: '0.2rem', flexShrink: 0, marginTop: 2,
  },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12,
    padding: '0.15rem 0.5rem', fontSize: '0.75rem', color: NAVY,
  },
  pillClose: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1',
    fontSize: '0.85rem', padding: 0, lineHeight: 1,
  },
};

function UserPicker({ value, onChange, multi = false, teamMembers }) {
  return (
    <select
      style={{ ...s.select, flex: 1 }}
      value={multi ? '' : (value || '')}
      onChange={e => {
        const uid = e.target.value;
        if (!uid) return;
        if (multi) {
          const current = Array.isArray(value) ? value : [];
          if (!current.includes(uid)) onChange([...current, uid]);
        } else {
          onChange(uid);
        }
      }}
    >
      <option value="">Select user…</option>
      {teamMembers.map(m => (
        <option key={m.id} value={m.id}>{m.name || m.email}</option>
      ))}
    </select>
  );
}

// base: the stage's own place, /api/workflow-templates/:id, /api/crm-boards/:id or /api/projects/:id
// (2026-10-22: a board owns its steps; this saved only to a template's stages).
export default function ActionRow({ action, stageId, base, onSave, onUpdate, onDelete, readOnly }) {
  const [trigger, setTrigger]     = useState(action.trigger     || 'ENTER');
  const [actionType, setType]     = useState(action.actionType  || 'NOTIFY_ASSIGNEE');
  const [config, setConfig]       = useState(action.config      || {});
  const [isActive, setActive]     = useState(action.isActive    ?? true);
  const [saving, setSaving]       = useState(false);
  const [teamMembers, setTeam]    = useState([]);
  const [error, setError]         = useState('');
  const [savedEmails, setSaved]   = useState(null);   // the person's saved emails written for a contact

  useEffect(() => {
    api('/api/team').then(r => r.ok ? r.json() : null).then(d => {
      if (d?.members) setTeam(d.members);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (actionType !== 'SEND_EMAIL_TEMPLATE' || savedEmails) return;
    api('/api/email-templates/named').then(r => r.json()).then(d => {
      setSaved((d?.templates || []).filter(t => t.context == null || t.context === 'contact'));
    }).catch(() => setSaved([]));
  }, [actionType, savedEmails]);

  async function save(patch) {
    if (!base || !stageId) return;
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = { trigger, actionType, config, isActive, ...patch };
      if (action.id) {
        const r = await api(`${base}/stages/${stageId}/actions/${action.id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
        const d = await r.json().catch(() => ({}));
        if (onUpdate && d.action) onUpdate(d.action);
      } else {
        const r = await api(`${base}/stages/${stageId}/actions`, {
          method: 'POST', body: JSON.stringify(payload),
        });
        const d = await r.json();
        if (onSave) onSave(d.action);
      }
    } catch (err) {
      setError(messageFromError(err, 'The step was not saved.'));
    } finally {
      setSaving(false);
    }
  }

  function setField(field, val) {
    const patch = {};
    if (field === 'trigger')    { setTrigger(val);  patch.trigger    = val; }
    if (field === 'actionType') { setType(val);      patch.actionType = val; setConfig({}); patch.config = {}; }
    if (field === 'isActive')   { setActive(val);    patch.isActive   = val; }
    if (field === 'config')     { setConfig(val);    patch.config     = val; }
    save(patch);
  }

  async function remove() {
    if (!base || !stageId || !action.id) { onDelete?.(); return; }
    try {
      await api(`${base}/stages/${stageId}/actions/${action.id}`, { method: 'DELETE' });
      onDelete?.();
    } catch (err) { setError(messageFromError(err, 'The step was not removed.')); }
  }

  function cfgField(key, val) { setField('config', { ...config, [key]: val }); }

  function renderConfigFields() {
    if (actionType === 'NOTIFY_CUSTOM') {
      const ids = Array.isArray(config.userIds) ? config.userIds : [];
      return (
        <div style={s.field}>
          <span style={s.label}>Users to notify</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
            {ids.map(uid => {
              const m = teamMembers.find(x => x.id === uid);
              return (
                <span key={uid} style={s.pill}>
                  {m?.name || uid}
                  {!readOnly && (
                    <button style={s.pillClose} onClick={() => cfgField('userIds', ids.filter(x => x !== uid))}>×</button>
                  )}
                </span>
              );
            })}
          </div>
          {!readOnly && (
            <UserPicker multi value={ids} onChange={v => cfgField('userIds', v)} teamMembers={teamMembers} />
          )}
        </div>
      );
    }

    if (actionType === 'AUTO_ASSIGN') {
      return (
        <div style={s.field}>
          <span style={s.label}>Assign to</span>
          <UserPicker value={config.userId} onChange={v => cfgField('userId', v)} teamMembers={teamMembers} />
        </div>
      );
    }

    if (actionType === 'SET_DUE_DATE_OFFSET') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="number" min={0} style={{ ...s.input, width: 56, flex: 'none' }}
            value={config.days ?? 0}
            onChange={e => cfgField('days', parseInt(e.target.value) || 0)}
            disabled={readOnly}
          />
          <span style={{ fontSize: '0.82rem', color: '#555' }}>days after entering stage</span>
        </div>
      );
    }

    if (actionType === 'WEBHOOK') {
      return (
        <div style={s.field}>
          <span style={s.label}>Webhook URL</span>
          <input
            type="url" style={s.input} placeholder="https://…"
            value={config.url || ''}
            onChange={e => cfgField('url', e.target.value)}
            disabled={readOnly}
          />
        </div>
      );
    }

    if (actionType === 'SEND_EMAIL_TEMPLATE') {
      // 2026-10-22: it sends (lib/stageEmail). One of the person's saved emails written for a contact.
      if (savedEmails === null) return <span style={{ fontSize: '0.78rem', color: '#555' }}>Loading your saved emails…</span>;
      return (
        <div style={s.field}>
          <span style={s.label}>Saved email</span>
          {savedEmails.length ? (
            <select style={{ ...s.select, maxWidth: '100%' }} aria-label="Saved email" disabled={readOnly}
              value={config.templateId != null ? String(config.templateId) : ''}
              onChange={e => e.target.value && cfgField('templateId', Number(e.target.value))}>
              <option value="">Choose a saved email…</option>
              {savedEmails.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
            </select>
          ) : (
            <span style={{ fontSize: '0.78rem', color: '#555' }}>No saved emails written for a contact yet. Make one in Settings, Email templates.</span>
          )}
          <span style={{ fontSize: '0.75rem', color: '#555' }}>
            Emails the card's contact from your mailbox. Not sent if the move is more than 24 hours old, or twice in a day.
          </span>
        </div>
      );
    }

    return null;
  }

  return (
    <div style={s.row}>
      {/* Trigger */}
      <div style={s.field}>
        <span style={s.label}>Trigger</span>
        <select style={s.select} aria-label="Trigger" value={trigger} disabled={readOnly}
          onChange={e => setField('trigger', e.target.value)}>
          {TRIGGER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Action type */}
      <div style={s.field}>
        <span style={s.label}>Action</span>
        <select style={s.select} aria-label="Action" value={actionType} disabled={readOnly}
          onChange={e => setField('actionType', e.target.value)}>
          {ACTION_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Config */}
      <div style={{ flex: '1 1 12rem', minWidth: 0 }}>{renderConfigFields()}</div>

      {/* Active toggle */}
      <div style={{ ...s.field, alignItems: 'center' }}>
        <span style={s.label}>Active</span>
        <button style={s.toggle(isActive)} disabled={readOnly}
          onClick={() => !readOnly && setField('isActive', !isActive)}>
          <div style={s.toggleThumb(isActive)} />
        </button>
      </div>

      {/* Delete */}
      {!readOnly && (
        <button style={s.deleteBtn} title="Delete automation" aria-label="Delete automation" onClick={remove}>🗑</button>
      )}
      {error && <p role="alert" style={{ flexBasis: '100%', margin: 0, fontSize: '0.8rem', color: '#b91c1c' }}>{error}</p>}
    </div>
  );
}
